import React from 'react'
import styled from 'styled-components'
import { color, typography } from '@primer-io/goat'

import Clickable from 'components/Clickable'
import PrimerLogo from 'assets/svg/primer-logo.svg'
import Flex from 'ds/Flex'
import Section from 'components/Section'
import { BodySmall } from 'ds/TextGoat'

const colSize = 180

const columns = [
	{
		title: 'Product',
		links: [
			{ text: 'Universal Checkout', url: 'https://primer.io/product/universal-checkout' },
			{ text: 'Payment Workflows', url: 'https://primer.io/product/payments-workflows' },
			{ text: 'Observability', url: 'https://primer.io/product/observability' },
			{ text: 'Apps & Integrations', url: 'https://primer.io/apps-integrations' },
		],
	},
	{
		title: 'Developers',
		links: [
			{ text: 'Primer Docs', url: 'https://primer.io/docs' },
			{ text: 'API Reference', url: 'https://primer.io/docs/api' },
			{ text: 'Service Status', url: 'https://status.primer.io' },
		],
	},
	{
		title: 'Company',
		links: [
			{ text: 'Blog', url: 'https://primer.io/blog' },
			{ text: 'Case Studies', url: 'https://primer.io/case-studies' },
			{ text: 'Team', url: 'https://primer.io/team' },
			{ text: 'Careers', url: 'https://primer.io/careers' },
		],
	},
	{
		title: 'Resources',
		links: [
			{ text: 'Terms', url: 'https://primer.io/terms' },
			{ text: 'Privacy', url: 'https://primer.io/privacy' },
		],
	},
]

const socialLinks = [
	{ text: 'LinkedIn', url: 'https://www.linkedin.com/company/primerapi' },
	{ text: 'YouTube', url: 'https://www.youtube.com/channel/UCblj8Ya4PGjRbKVXmVwKyeQ' },
	{ text: 'Spotify', url: 'https://podcasters.spotify.com/pod/show/primer-payments' },
]

const Logo = styled(PrimerLogo)`
	width: 95px;
	margin: 70px 0 44px;
	${p => p.theme.above('large')`
    margin-top: 10px;
    width: 120px;
  `}
`

const Column = styled.div`
	display: none;
	${p => p.theme.above('large')`
    ${typography('body-medium')}
    display: block;
    width: ${colSize}px;
    margin-top: 0;
    &:not(:last-child) {
      margin-bottom: 36px;
    }
  `}
`

const ColumnTitle = styled.h5`
	font-weight: 500;
	margin-bottom: 12px;
`

const SocialLinksColumn = styled(Column)`
	display: block;
	max-width: 160px;
	position: relative;
	top: -6px;
`

const Link = styled(Clickable)`
	display: block;
	position: relative;
	line-height: 1.96;
	color: ${color('gray-700')};
	transition: color 0.2s ease;
	&:hover,
	&:focus {
		color: ${color('gray-900')};
	}
`

const SocialLink = styled(Link)`
	color: ${color('gray-900')};
	font: 16px / 1.9 'Inter';
	letter-spacing: -0.15px;
	&:hover,
	&:focus {
		color: ${color('coral-500')};
	}
	${p => p.theme.above('large')`
    font: 24px / 1.66 'Inter';
    letter-spacing: -0.02em;
  `}
`

const EmailLink = styled(SocialLink)`
	${typography('body-small')}
	color: ${color('gray-700')};

	${p => p.theme.above('large')`
    line-height: 1.1;
    color: ${color('gray-900')};
  `}
`

const Footer = () => (
	<footer>
		<Clickable to='https://primer.io' aria-label='Primer.io'>
			<Logo />
		</Clickable>

		<Flex justify='space-between'>
			{columns.map(({ title, links }) => (
				<Column key={title}>
					<ColumnTitle>{title}</ColumnTitle>
					{links.map(({ text, url }) => (
						<Link to={url} key={url}>
							{text}
						</Link>
					))}
				</Column>
			))}
			<SocialLinksColumn>
				{socialLinks.map(({ text, url }) => (
					<Link to={url} key={url}>
						{text}
					</Link>
				))}
			</SocialLinksColumn>
		</Flex>

		<Flex flow='row nowrap' align='flex-end'>
			<BodySmall as='p'>© Primer {new Date().getFullYear()}</BodySmall>
			<EmailLink to='mailto:hello@primer.io'>hello@primer.io</EmailLink>
		</Flex>
	</footer>
)

export default Footer
